import React from 'react';
import { navigate } from '@gatsbyjs/reach-router';
import Catalog from '~/Catalog';
import Button from '~/components/atom/Button';
import JustificationContainer from '~/components/atom/JustificationContainer';
import TextButton from '~/components/atom/TextButton';
import { Heading3 } from '~/components/atom/Typography';
import Dropdown from '~/components/molecule/Dropdown';
import Modal from '~/components/organism/ModalsV2/Modal';
import Overlay from '~/components/organism/ModalsV2/Overlay';
import { useUpdateContactMutation } from '~/graphql/types';
import useAddToast from '~/hooks/useAddToast';
import useCurrentAccount from '~/hooks/useCurrentAccount';
import { getErrorType } from '~/util/errorHandling';
import formatToastMessage from '~/util/formatToastMessage';
import useOfficeUserSelection from '~/hooks/useOfficeUserSelection';
import { ErrorTypes } from '~/Errors';

const text = {
  header: 'Kantoor en gebruiker toewijzen',
  noUser: 'Alle gebruikers',
  officeFieldLabel: 'Wijs vestiging toe',
  userFieldLabel: 'Wijs gebruiker toe',
  assignButtonLabel: 'Toewijzen',
  cancelButtonLabel: 'Annuleren',
  contactIsDeletedError: 'Dit contact is verwijderd',
};

const MODAL_WIDTH = '600px';

type Props = {
  contactId: string;
  name: string;
  officeId?: string | null;
  userId?: string | null;
  onClose: () => void;
  refetch: () => void;
};

const AssignOfficeUserModal: React.FCC<Props> = ({
  onClose,
  contactId,
  refetch,
  name,
  officeId,
  userId,
}) => {
  const { id: accountId } = useCurrentAccount();
  const addToast = useAddToast();
  const [updateContact, { loading }] = useUpdateContactMutation({});

  const {
    officeOptions,
    selectedOfficeIdx,
    onOfficeChange,
    selectedOffice,
    userOptions,
    selectedUserIdx,
    onUserChange,
    selectedUser,
  } = useOfficeUserSelection({
    withAllUsersOption: true,
    initialOfficeId: officeId,
    initialUserId: userId,
  });

  const onAssign = async () => {
    const { data, errors } = await updateContact({
      variables: {
        accountId,
        id: contactId,
        update: {
          officeId: selectedOffice?.id,
          userId: selectedUser === null ? null : selectedUser?.id,
        },
      },
    });

    if (data && !errors) {
      onClose();
      void refetch();
      return addToast([
        formatToastMessage(Catalog.genericSuccessMessage, 'success'),
      ]);
    }

    if (errors) {
      errors.forEach(error => {
        const errorType = getErrorType(error);

        if (errorType && errorType.includes(ErrorTypes.notFoundError)) {
          addToast([formatToastMessage(text.contactIsDeletedError, 'danger')]);

          return navigate('/-/contacts');
        }

        return addToast([
          formatToastMessage(Catalog.genericUnknownErrorMessage, 'danger'),
        ]);
      });
    }
  };
  return (
    <Overlay onClose={onClose}>
      <Modal onClose={onClose} maxWidth={MODAL_WIDTH}>
        <JustificationContainer
          width="100%"
          direction="column"
          align="center"
          padding={['xl']}
          gap="m"
        >
          <Heading3 color={{ group: 'primary' }}>
            {text.header}: {name}
          </Heading3>
          <JustificationContainer width="100%" direction="column" gap="base">
            <Dropdown
              label={text.officeFieldLabel}
              options={officeOptions}
              selectedOptionIdx={selectedOfficeIdx}
              onChange={onOfficeChange}
            />

            <Dropdown
              label={text.userFieldLabel}
              options={userOptions}
              selectedOptionIdx={selectedUserIdx}
              onChange={onUserChange}
            />
          </JustificationContainer>
          <JustificationContainer
            width="100%"
            align="center"
            justification="space-between"
            margin={['base', null, null, null]}
          >
            <TextButton
              size="medium"
              appearance="danger"
              label={text.cancelButtonLabel}
              onClick={onClose}
              padding={[null]}
            />
            <Button
              size="medium"
              appearance="secondary"
              disabled={loading}
              loading={loading}
              onClick={onAssign}
              label={text.assignButtonLabel}
            />
          </JustificationContainer>
        </JustificationContainer>
      </Modal>
    </Overlay>
  );
};

export default AssignOfficeUserModal;
